import { useState, useCallback } from 'react';
import { deliveryHistoryDetailApi } from '../api/deliveryHistoryDetailApi';
import { DeliveryHistoryDetailFilters, DeliveryHistoryDetailRow, RiderSummary } from '../types/deliveryHistoryDetail.types';
import { DeliveryHistoryDetailRawItem } from '../types/deliveryHistoryDetail.api.types';
import { normalizeDetailRow } from '../utils/deliveryHistoryDetailHelpers';

export const useDeliveryDetailData = (filters: DeliveryHistoryDetailFilters) => {
    const [results, setResults] = useState<DeliveryHistoryDetailRow[]>([]);
    const [riderSummaries, setRiderSummaries] = useState<RiderSummary[]>([]);
    const [summaryInfo, setSummaryInfo] = useState<{ totalDeliveryAmt?: string; totalDeliveryCount?: number }>({});
    const [isSummaryView, setIsSummaryView] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [contextInfo, setContextInfo] = useState({ suppId: '', suppName: '', riderId: '', riderName: '' });

    const fetchDetails = useCallback(
        async (overrideFilters?: DeliveryHistoryDetailFilters) => {
            const requestFilters = overrideFilters ?? filters;

            if (!requestFilters.suppId) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                setContextInfo({
                    suppId: requestFilters.suppId,
                    suppName: requestFilters.suppName ?? '',
                    riderId: requestFilters.riderId || '',
                    riderName: requestFilters.riderId ? (requestFilters.riderName ?? '') : '',
                });

                const response = await deliveryHistoryDetailApi.getDetails(requestFilters);

                if (!requestFilters.riderId && !Array.isArray(response) && response?.riderSummaries) {
                    setIsSummaryView(true);
                    setRiderSummaries(response.riderSummaries);
                    setSummaryInfo({
                        totalDeliveryAmt: response.totalDeliveryAmt,
                        totalDeliveryCount: response.totalDeliveryCount,
                    });
                    setResults([]);
                    return;
                }

                setIsSummaryView(false);
                const items: DeliveryHistoryDetailRawItem[] = Array.isArray(response) ? response : (response?.items ?? []);
                const normalized = items.map(normalizeDetailRow);
                setResults(normalized);
                setRiderSummaries([]);
                setSummaryInfo({});

                if (normalized.length > 0) {
                    setContextInfo((prev) => ({
                        suppId: prev.suppId || normalized[0].suppId,
                        suppName: prev.suppName || normalized[0].suppName || '',
                        riderId: requestFilters.riderId ? (prev.riderId || normalized[0].riderId) : '',
                        riderName: requestFilters.riderId ? (prev.riderName || normalized[0].riderName || '') : '',
                    }));
                }
            } catch {
                setError('배달 내역 상세를 불러오는데 실패했습니다.');
                setResults([]);
                setRiderSummaries([]);
                setSummaryInfo({});
                setIsSummaryView(false);
            } finally {
                setLoading(false);
            }
        },
        [filters]
    );

    return { results, riderSummaries, summaryInfo, isSummaryView, loading, error, contextInfo, fetchDetails };
};
